import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input as InputBase } from '../Input';
import { Select as SelectBase } from '../Select';
import { Textarea as TextareaBase } from '../Textarea';
import { Checkbox as CheckboxBase } from '../Checkbox';
import { Button as ButtonBase } from '../Button';
import { Field as FieldBase, Label as LabelBase } from '../Fieldset';

// Cast untyped JS components to accept all props
const Input = InputBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'input'>
>;
const Select = SelectBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'select'> & { options?: unknown[] }
>;
const Textarea = TextareaBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'textarea'>
>;
const Checkbox = CheckboxBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'input'>
>;
const Button = ButtonBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'button'>
>;
const Field = FieldBase as React.ComponentType<{
  children?: React.ReactNode;
  className?: string;
}>;
const Label = LabelBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'label'>
>;
import AIGuidance from '../AIGuidance';
import FileUpload from '../FileUpload';
import { PPTProcessingStatus } from '../QualificationForm/PPTProcessingStatus';
import { usePPTTranscription } from '../QualificationForm/hooks/usePPTTranscription';
import { FormValidator } from 'forms';
import type { SchemaField } from '../QualificationForm/types';

interface Schema {
  fields: SchemaField[];
}

interface DynamicFormProps {
  schema: Schema;
  onSubmit: (data: Record<string, unknown>) => void;
  isSubmitting?: boolean;
  category?: string;
  rubricData?: unknown;
}

interface PresentationState {
  url: string | null;
  fileName: string | null;
  uploading: boolean;
}

type FormData = Record<string, string | boolean | string[]>;
type FormErrors = Record<string, string[]>;

function DynamicForm({
  schema,
  onSubmit,
  isSubmitting = false,
  category,
  rubricData,
}: DynamicFormProps) {
  const [presentation, setPresentation] = useState<PresentationState>({
    url: null,
    fileName: null,
    uploading: false,
  });
  const [fileError, setFileError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const showDetail = searchParams.get('detail') === 'true';

  const { pptTranscription, startPPTTranscription } = usePPTTranscription(
    category ?? ''
  );

  const handleFileUploadStart = () =>
    setPresentation(prev => ({ ...prev, uploading: true }));

  const handleFileUploaded = (url: string, fileName: string) =>
    setPresentation({ url, fileName, uploading: false });

  const handleFileUploadError = () =>
    setPresentation(prev => ({ ...prev, uploading: false }));

  const handleFileSelected = (file: File) => startPPTTranscription(file);

  const [formData, setFormData] = useState<FormData>(() => {
    const initial: FormData = {};
    schema.fields.forEach(f => {
      if (f.type === 'array') {
        initial[f.name] = [''];
      } else if (f.type === 'checkbox') {
        initial[f.name] = false;
      } else {
        // Use defaultValue if provided, otherwise empty string
        initial[f.name] = (f.defaultValue as string) || '';
      }
    });
    return initial;
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Auto-populate detail textarea fields from PPT extraction when complete
  useEffect(() => {
    if (!pptTranscription.isCompleted || !pptTranscription.extractedData)
      return;

    const sections = pptTranscription.extractedData
      .split(/===\s*([^=]+)\s*===/)
      .reduce<
        Array<{ title: string; content: string }>
      >((acc, part, i, arr) => {
        if (i % 2 === 1) {
          acc.push({ title: part.trim(), content: arr[i + 1]?.trim() || '' });
        }
        return acc;
      }, []);

    const textareaDetailFields = schema.fields
      .slice(7)
      .filter(
        f =>
          f.type === 'textarea' &&
          !f.readonly &&
          f.name !== 'additional_comments'
      );

    setFormData(prev => {
      const updated = { ...prev };
      sections.forEach((section, i) => {
        const field = textareaDetailFields[i];
        if (field && section.content) {
          updated[field.name] = section.content
            .split('•')
            .filter(p => p.trim())
            .map(p => p.trim())
            .join('\n\n');
        }
      });
      return updated;
    });
  }, [pptTranscription.isCompleted, pptTranscription.extractedData]);

  // Update form data when schema changes to ensure correct default values
  useEffect(() => {
    setFormData(prev => {
      const updated = { ...prev };
      schema.fields.forEach(f => {
        if (
          f.defaultValue !== undefined &&
          updated[f.name] !== f.defaultValue
        ) {
          updated[f.name] = f.defaultValue as string | boolean;
        }
      });
      return updated;
    });
  }, [schema]);

  const handleChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleArrayChange = (name: string, idx: number, value: string) => {
    setFormData(prev => {
      const arr = [...(prev[name] as string[])];
      arr[idx] = value;
      return { ...prev, [name]: arr };
    });
  };

  const addArrayItem = (name: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: [...(prev[name] as string[]), ''],
    }));
  };

  const removeArrayItem = (name: string, idx: number) => {
    setFormData(prev => {
      const arr = [...(prev[name] as string[])];
      arr.splice(idx, 1);
      return { ...prev, [name]: arr };
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (presentation.uploading || pptTranscription.isTranscribing) {
      setFileError('Please wait for the file to finish processing.');
      return;
    }
    if (!presentation.url) {
      setFileError('Presentation file (PDF) is required.');
      return;
    }
    setFileError(null);

    const validator = new FormValidator();
    const result = validator.validateFormData(formData, schema);
    if (result.isValid) {
      setErrors({});
      onSubmit({ ...result.validatedData, award_material: presentation.url });
    } else {
      setErrors(result.errors);
    }
  };

  // Helper function to get the display value for a field
  const getDisplayValue = (field: SchemaField): string => {
    if (field.readonly && field.displayValue) {
      return field.displayValue;
    }
    return formData[field.name] as string;
  };

  // Clean, minimal input styling
  const getInputClass = (fieldName: string, baseClass = ''): string => {
    const hasError = errors[fieldName];
    const baseStyles =
      'w-full px-4 py-3 rounded-lg transition-all duration-200 text-gray-900 font-baijamjuree placeholder-gray-500 bg-white';

    if (hasError) {
      return `${baseStyles} border-2 border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 ${baseClass}`;
    } else {
      return `${baseStyles} border border-gray-300 focus:border-gray-500 focus:ring-2 focus:ring-gray-200 hover:border-gray-400 ${baseClass}`;
    }
  };

  // Split fields into logical groups
  const contactFields = schema.fields.slice(0, 7);
  const detailFields = schema.fields.slice(7);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Contact Information */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {contactFields
            .filter(f => f.type !== 'hidden')
            .map(field => (
              <Field
                key={field.name}
                className={
                  field.type === 'select' && field.name === 'pillar'
                    ? 'md:col-span-3'
                    : field.name.includes('project_name') ||
                        field.name === 'leadership_achievement'
                      ? 'md:col-span-2'
                      : ''
                }
              >
                <Label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-gray-700 mb-2 font-baijamjuree"
                >
                  {field.label}
                  {field.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </Label>
                {field.type === 'array' ? (
                  <div className="space-y-3">
                    {(formData[field.name] as string[]).map((val, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <Input
                          id={`${field.name}-${idx}`}
                          value={val}
                          onChange={e =>
                            handleArrayChange(field.name, idx, e.target.value)
                          }
                          className={getInputClass(field.name, 'flex-1')}
                          placeholder={field.placeholder}
                        />
                        <button
                          type="button"
                          onClick={() => removeArrayItem(field.name, idx)}
                          className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center transition-all text-sm"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayItem(field.name)}
                      className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-3 py-2 transition-all"
                    >
                      + Add Item
                    </button>
                  </div>
                ) : field.type === 'select' ? (
                  <Select
                    id={field.name}
                    value={formData[field.name] as string}
                    onChange={e => handleChange(field.name, e.target.value)}
                    options={field.options || []}
                    className={getInputClass(field.name)}
                  />
                ) : field.type === 'textarea' ? (
                  <Textarea
                    id={field.name}
                    value={getDisplayValue(field)}
                    onChange={e => handleChange(field.name, e.target.value)}
                    className={getInputClass(
                      field.name,
                      'min-h-[100px] resize-y'
                    )}
                    readOnly={field.readonly}
                    placeholder={field.placeholder}
                  />
                ) : field.type === 'checkbox' ? (
                  <div className="flex items-center space-x-3 mt-2">
                    <Checkbox
                      id={field.name}
                      checked={formData[field.name] as boolean}
                      onChange={e => handleChange(field.name, e.target.checked)}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-500/50 border-gray-300 rounded"
                    />
                    <Label
                      htmlFor={field.name}
                      className="text-sm text-gray-700 font-baijamjuree"
                    >
                      {field.placeholder || field.label}
                    </Label>
                  </div>
                ) : (
                  <Input
                    id={field.name}
                    value={getDisplayValue(field)}
                    onChange={e => handleChange(field.name, e.target.value)}
                    className={getInputClass(field.name)}
                    readOnly={field.readonly}
                    placeholder={field.placeholder}
                  />
                )}
                {errors[field.name] && (
                  <div className="mt-2 text-sm text-red-600 font-baijamjuree">
                    {errors[field.name].join(', ')}
                  </div>
                )}
              </Field>
            ))}
        </div>
      </div>

      {/* Presentation File Upload */}
      <div className="space-y-4 ">
        <FileUpload
          label="Upload Project (PDF, PPT)"
          acceptedTypes={['pdf', 'ppt', 'pptx']}
          onFileSelected={handleFileSelected}
          onFileUploaded={handleFileUploaded}
          onUploadStart={handleFileUploadStart}
          onUploadError={handleFileUploadError}
          isUploading={presentation.uploading}
          uploadedFileName={presentation.fileName}
          uploadedFileUrl={presentation.url}
          required
          maxSizeGB={0.1}
        />

        {(pptTranscription.isTranscribing ||
          pptTranscription.isCompleted ||
          !!pptTranscription.error) && (
          <PPTProcessingStatus
            pptTranscription={pptTranscription}
            showExtractedContent={false}
          />
        )}

        {fileError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-sm text-red-600 font-baijamjuree">{fileError}</p>
          </div>
        )}
      </div>

      {/* Detailed Information */}
      {detailFields.length > 0 && (
        <div className={`space-y-4 ${showDetail ? '' : 'hidden'}`}>
          <div className="space-y-4">
            {detailFields
              .filter(f => f.type !== 'hidden')
              .map(field => (
                <Field key={field.name}>
                  <div className="flex items-start justify-between mb-2">
                    <Label
                      htmlFor={field.name}
                      className="block text-sm font-medium text-gray-700 font-baijamjuree"
                    >
                      {field.label}
                      {field.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </Label>
                    {field.type === 'textarea' &&
                      category &&
                      !field.name.includes('project_name') &&
                      !field.name.includes('additional_comments') && (
                        <div className="ml-2 flex-shrink-0">
                          <AIGuidance
                            fieldName={field.name}
                            fieldLabel={field.label}
                            currentContent={formData[field.name] as string}
                            category={category}
                            rubricData={rubricData}
                          />
                        </div>
                      )}
                  </div>
                  {field.type === 'array' ? (
                    <div className="space-y-3">
                      {(formData[field.name] as string[]).map((val, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <Input
                            id={`${field.name}-${idx}`}
                            value={val}
                            onChange={e =>
                              handleArrayChange(field.name, idx, e.target.value)
                            }
                            className={getInputClass(field.name, 'flex-1')}
                            placeholder={field.placeholder}
                          />
                          <button
                            type="button"
                            onClick={() => removeArrayItem(field.name, idx)}
                            className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center transition-all text-sm"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem(field.name)}
                        className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-3 py-2 transition-all"
                      >
                        + Add Item
                      </button>
                    </div>
                  ) : field.type === 'select' ? (
                    <Select
                      id={field.name}
                      value={formData[field.name] as string}
                      onChange={e => handleChange(field.name, e.target.value)}
                      options={field.options || []}
                      className={getInputClass(field.name)}
                    />
                  ) : field.type === 'textarea' ? (
                    <Textarea
                      id={field.name}
                      value={getDisplayValue(field)}
                      onChange={e => handleChange(field.name, e.target.value)}
                      className={getInputClass(
                        field.name,
                        'min-h-[120px] resize-y'
                      )}
                      readOnly={field.readonly}
                      placeholder={field.placeholder}
                    />
                  ) : field.type === 'checkbox' ? (
                    <div className="flex items-center space-x-3 mt-2">
                      <Checkbox
                        id={field.name}
                        checked={formData[field.name] as boolean}
                        onChange={e =>
                          handleChange(field.name, e.target.checked)
                        }
                        className="h-4 w-4 text-gray-900 focus:ring-gray-500/50 border-gray-300 rounded"
                      />
                      <Label
                        htmlFor={field.name}
                        className="text-sm text-gray-700 font-baijamjuree"
                      >
                        {field.placeholder || field.label}
                      </Label>
                    </div>
                  ) : (
                    <Input
                      id={field.name}
                      value={getDisplayValue(field)}
                      onChange={e => handleChange(field.name, e.target.value)}
                      className={getInputClass(field.name)}
                      readOnly={field.readonly}
                      placeholder={field.placeholder}
                    />
                  )}
                  {errors[field.name] && (
                    <div className="mt-2 text-sm text-red-600 font-baijamjuree">
                      {errors[field.name].join(', ')}
                    </div>
                  )}
                </Field>
              ))}
          </div>
        </div>
      )}

      {/* Submit Section */}
      <div className="border-t border-gray-200 pt-8">
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-600 font-baijamjuree">
              Please review all information before submitting
            </p>
          </div>
          <Button
            type="submit"
            className="w-full sm:w-auto bg-black hover:bg-gray-800 text-white rounded-full px-10 py-4 font-baijamjuree-bold shadow-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={
              isSubmitting ||
              presentation.uploading ||
              pptTranscription.isTranscribing
            }
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Submitting...</span>
              </div>
            ) : (
              'Submit Application'
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default DynamicForm;
